import React from 'react'
import PropTypes from 'prop-types'
import { Button, paymentMethodsIconPack, Logo, Heading, Text, subscriptionsLogoPack, IconManager, Divider, Clipped, Box, Price, Level } from '@deseretbook/react-ui'
import { usePopup } from '@deseretbook/react-utils'
import PaymentSourceDetails from './payment/PaymentSourceDetails'
import PlatinumPointsDisplay from '../PlatinumPointsValue'
import { SignupModal } from '../subscriptions'

IconManager.register(paymentMethodsIconPack)
IconManager.register(subscriptionsLogoPack)

/**
 * Order confirmation sidebar content
 *
 * @returns {function} Component
 */
const ConfirmationSidebarContent = ({
  order,
  user,
  payments,
  recentlyCompleted,
}) => {
  /* If user is a current platinum member */
  const currentDate = new Date().toISOString().slice(0, 10)
  const hasEarnablePlatinumPoints = order.earnablePlatinumPoints > 0
  const isPlatinum = user && (user['platinum?'] || !!user.subscriptions
    .filter(({
      subscriptionPlan, awaitingCompletion, serviceableUntil, status,
    }) => !awaitingCompletion && (serviceableUntil >= currentDate || status === 'Active') && subscriptionPlan && subscriptionPlan.name === 'Platinum Rewards Membership')
    .length)

    const popup = usePopup()

  /**
   * If user is a newly signed up platinum member after placing the order.
   * This is needed for removing the Platinum signup callout afer signing up
   * because they technically are platinum now but they did not earn points
   * on this order and we use `isPlatinum` to render points earned on the order
   */
  const [isNewPlatinum, setIsNewPlatinum] = React.useState(false)

  return (
    <React.Fragment>
      {/* Order cost breakdown */}
      <Level>
        <Level.Left isShrinkable>
          <Level.Item>
            <Text hasEllipsis>Subtotal</Text>
          </Level.Item>
        </Level.Left>
        <Level.Right>
          <Level.Item>
            <Text hasEllipsis><Price value={order.itemTotal} /></Text>
          </Level.Item>
        </Level.Right>
      </Level>
      <Level>
        <Level.Left isShrinkable>
          <Level.Item>
            <Clipped>
              <Text hasEllipsis>Shipping</Text>
              {order.shipments.map(shipment => (
                <Text key={shipment.id} hasEllipsis size="tiny">{shipment.shippingMethod.name}</Text>
              ))}
            </Clipped>
          </Level.Item>
        </Level.Left>
        <Level.Right>
          <Level.Item>
            <Text hasEllipsis><Price value={order.shipmentTotal} /></Text>
          </Level.Item>
        </Level.Right>
      </Level>
      {/* Order adjustments and discounts */}
      {order.allAdjustments
        .filter(({ eligible, sourceType }) => eligible && (sourceType === 'Spree::PromotionAction' || sourceType === 'Spree::StockLocation'))
        .map(({
          id,
          label,
          amount,
          promotionCode,
        }) => (
          <Level key={id}>
            <Clipped>
              <Level.Left>
                <Level.Item>
                  <Text hasEllipsis>
                    {(promotionCode && promotionCode.value)
                      ? `Promotion (${promotionCode.value})`
                      : label}
                  </Text>
                </Level.Item>
              </Level.Left>
            </Clipped>
            <Level.Right>
              <Level.Item>
                <Text hasEllipsis><Price value={amount} /></Text>
              </Level.Item>
            </Level.Right>
          </Level>
        ))}
      <Level>
        <Level.Left isShrinkable>
          <Level.Item>
            <Text hasEllipsis>Taxes & Fees</Text>
          </Level.Item>
        </Level.Left>
        <Level.Right>
          <Level.Item>
            <Text hasEllipsis><Price value={order.taxTotal} /></Text>
          </Level.Item>
        </Level.Right>
      </Level>
      <Divider />
      {/* Order total */}
      <Level>
        <Level.Left isShrinkable>
          <Level.Item>
            <Text hasEllipsis size="large">Total</Text>
          </Level.Item>
        </Level.Left>
        <Level.Right>
          <Level.Item>
            <Text hasEllipsis isBold size="large"><Price value={order.total} /></Text>
          </Level.Item>
        </Level.Right>
      </Level>
      {/* Order payments */}
      {payments && (
        <Level.Left>
          <Level.Item>
            <Box paddingTop={1}>
              {payments.map(payment => (
                <PaymentSourceDetails key={payment.id} source={payment.source} />
              ))}
            </Box>
          </Level.Item>
        </Level.Left>
      )}
      {/* Order gift message */}
      {order.giftMessage && (
        <Box paddingTop={1}>
          <Heading>Gift Message</Heading>
          <Text>{order.giftMessage}</Text>
        </Box>
      )}
      {/* Platinum points earned on order */}
      {hasEarnablePlatinumPoints && user && isPlatinum && !isNewPlatinum && (
        <React.Fragment>
          <Divider />
          <Level>
            <Level.Left>
              <Level.Item>
                <Logo name="platinumCrest" size="medium" />
              </Level.Item>
            </Level.Left>
            <Level.Item>
              <Text>
                You earned <PlatinumPointsDisplay points={order.earnablePlatinumPoints} /> in
                Platinum Points
              </Text>
            </Level.Item>
          </Level>
        </React.Fragment>
      )}
      {/* Platinum signup callout and modal if not platinum and order recently completed */}
      {recentlyCompleted && user && !isPlatinum && (
        <Box className="hidden-print">
          <Box paddingTop={2} justifyContent="center">
            <Box maxWidth={20}>
              <Logo name="platinumRewards" />
            </Box>
          </Box>

          {hasEarnablePlatinumPoints && (
            <Text alignment="center" display="block">
              <Text isBold>
                Receive <PlatinumPointsDisplay points={order.earnablePlatinumPoints} /> back in
                points on this purchase
              </Text> and 10% back in every purchase going forward (plus many other perks too).
            </Text>
          )}
          {!hasEarnablePlatinumPoints && (
            <Text alignment="center" display="block">
              <Text isBold>
                Receive 10% back in points
              </Text>{' '}
              with every qualifying purchase going forward (plus many other perks too).
            </Text>
          )}
          <Box paddingTop={1} justifyContent="center">
            <Button color="platinum" type="button" {...popup.triggerProps}>
              Sign Up Now
            </Button>
          </Box>
        </Box>
      )}
      <SignupModal
        popupProps={popup.popupProps}
        user={user}
        onClose={popup.close}
        onSuccess={() => setIsNewPlatinum(true)}
        documentUID="checkout-platinum"
        documentType="subscription_signup_v1"
      />
      {/* Contact customer support section */}
      <Box paddingTop={4} paddingBottom={1} justifyContent="center">
        <Button
          type="button"
          color="light"
          className="hidden-print"
          onClick={() => {
            if (!window.CustomerSupport) {
              return
            }

            window.CustomerSupport.openChat()
            window.CustomerSupport.hideChatBubble()
          }}
        >
          Have Questions? Contact Us
        </Button>
      </Box>
      <Text size="tiny" color="muted" display="block" alignment="center">Order Number {order.number}</Text>
    </React.Fragment>
  )
}

ConfirmationSidebarContent.propTypes = {
  order: PropTypes.shape({
    number: PropTypes.string,
    itemTotal: PropTypes.string,
    shipments: PropTypes.arrayOf(PropTypes.shape({
      shippingMethod: PropTypes.shape({
        name: PropTypes.string,
      })
    })),
    shipmentTotal: PropTypes.string,
    allAdjustments: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
      amount: PropTypes.string,
      promotionCode: PropTypes.string,
    })),
    taxTotal: PropTypes.string,
    total: PropTypes.string,
    giftMessage: PropTypes.string,
    earnablePlatinumPoints: PropTypes.number,
  }).isRequired,
  payments: PropTypes.arrayOf(PropTypes.shape({})),
  recentlyCompleted: PropTypes.bool,
  user: PropTypes.shape({
    subscriptions: PropTypes.shape({}),
    'platinum?': PropTypes.bool,
  }),
}

ConfirmationSidebarContent.defaultProps = {
  payments: [],
}

export default ConfirmationSidebarContent
